/*
* === AcademicInfoStep.js (Enhanced - SA Institutions List) ===
* Features:
* - Pre-populated list of SA Universities, UoTs, and TVETs
* - Searchable Dropdown (Autocomplete)
* - Categorized display
*/
import React from 'react';
import { 
    TextField, Grid, Typography, FormControl, FormLabel, RadioGroup, 
    FormControlLabel, Radio, InputLabel, Select, MenuItem, FormHelperText, 
    Autocomplete, Box 
} from '@mui/material';
import { useOutletContext } from 'react-router-dom';

// --- DATA: South African Institutions ---
const INSTITUTIONS = [
    // Public Universities
    { label: "University of Cape Town (UCT)", category: "University" },
    { label: "University of the Witwatersrand (Wits)", category: "University" },
    { label: "Stellenbosch University", category: "University" },
    { label: "University of Pretoria (UP)", category: "University" },
    { label: "University of Johannesburg (UJ)", category: "University" },
    { label: "University of KwaZulu-Natal (UKZN)", category: "University" },
    { label: "University of the Western Cape (UWC)", category: "University" },
    { label: "Rhodes University", category: "University" },
    { label: "North-West University (NWU)", category: "University" },
    { label: "University of the Free State (UFS)", category: "University" },
    { label: "Nelson Mandela University (NMU)", category: "University" },
    { label: "University of Fort Hare", category: "University" },
    { label: "University of Limpopo", category: "University" },
    { label: "University of Venda", category: "University" },
    { label: "Walter Sisulu University (WSU)", category: "University" },
    { label: "University of Zululand", category: "University" },
    { label: "Sefako Makgatho Health Sciences University", category: "University" },
    { label: "University of Mpumalanga", category: "University" },
    { label: "Sol Plaatje University", category: "University" },
    { label: "University of South Africa (UNISA)", category: "University" },

    // Universities of Technology
    { label: "Cape Peninsula University of Technology (CPUT)", category: "University of Technology" },
    { label: "Durban University of Technology (DUT)", category: "University of Technology" },
    { label: "Tshwane University of Technology (TUT)", category: "University of Technology" },
    { label: "Vaal University of Technology (VUT)", category: "University of Technology" },
    { label: "Central University of Technology (CUT)", category: "University of Technology" },
    { label: "Mangosuthu University of Technology (MUT)", category: "University of Technology" },

    // TVET Colleges
    { label: "Boland TVET College", category: "TVET College" },
    { label: "Buffalo City TVET College", category: "TVET College" },
    { label: "Capricorn TVET College", category: "TVET College" },
    { label: "Central Johannesburg TVET College", category: "TVET College" },
    { label: "Coastal KZN TVET College", category: "TVET College" },
    { label: "College of Cape Town", category: "TVET College" },
    { label: "Eastcape Midlands TVET College", category: "TVET College" },
    { label: "Ekurhuleni East TVET College", category: "TVET College" },
    { label: "Ekurhuleni West TVET College", category: "TVET College" },
    { label: "Elangeni TVET College", category: "TVET College" },
    { label: "Esayidi TVET College", category: "TVET College" },
    { label: "False Bay TVET College", category: "TVET College" },
    { label: "Flavius Mareka TVET College", category: "TVET College" },
    { label: "Goldfields TVET College", category: "TVET College" },
    { label: "Gert Sibande TVET College", category: "TVET College" },
    { label: "Ikhala TVET College", category: "TVET College" },
    { label: "Ingwe TVET College", category: "TVET College" },
    { label: "King Hintsa TVET College", category: "TVET College" },
    { label: "King Sabata Dalindyebo TVET College", category: "TVET College" },
    { label: "Lephalale TVET College", category: "TVET College" },
    { label: "Lovedale TVET College", category: "TVET College" },
    { label: "Majuba TVET College", category: "TVET College" },
    { label: "Maluti TVET College", category: "TVET College" },
    { label: "Mnambithi TVET College", category: "TVET College" },
    { label: "Mopani South East TVET College", category: "TVET College" },
    { label: "Motheo TVET College", category: "TVET College" },
    { label: "Mthashana TVET College", category: "TVET College" },
    { label: "Nkangala TVET College", category: "TVET College" },
    { label: "Northern Cape Urban TVET College", category: "TVET College" },
    { label: "Northern Cape Rural TVET College", category: "TVET College" },
    { label: "Northlink TVET College", category: "TVET College" },
    { label: "Orbit TVET College", category: "TVET College" },
    { label: "Port Elizabeth TVET College", category: "TVET College" },
    { label: "Sekhukhune TVET College", category: "TVET College" },
    { label: "South Cape TVET College", category: "TVET College" },
    { label: "South West Gauteng TVET College", category: "TVET College" },
    { label: "Taletso TVET College", category: "TVET College" },
    { label: "Thekwini TVET College", category: "TVET College" },
    { label: "Tshwane North TVET College", category: "TVET College" },
    { label: "Tshwane South TVET College", category: "TVET College" },
    { label: "Umfolozi TVET College", category: "TVET College" },
    { label: "Umgungundlovu TVET College", category: "TVET College" },
    { label: "Vhembe TVET College", category: "TVET College" },
    { label: "Vuselela TVET College", category: "TVET College" },
    { label: "Waterberg TVET College", category: "TVET College" },
    { label: "West Coast TVET College", category: "TVET College" },
    { label: "Western TVET College", category: "TVET College" },
    
    // Fallback
    { label: "Other / Private Institution", category: "Other" }
];

const AcademicInfoStep = () => {
    const { formData, handleInputChange, errors } = useOutletContext();
    const data = formData.academic || {};

    // Helper to simulate standard event for Autocomplete
    const handleInstitutionChange = (event, newValue) => {
        // newValue will be the object { label: "...", category: "..." } or null
        const value = newValue ? newValue.label : '';
        handleInputChange('academic', { target: { name: 'institutionName', value: value } });
    };
    
    // For Intended Institution (Future)
    const handleIntendedInstitutionChange = (event, newValue) => {
        const value = newValue ? newValue.label : '';
        handleInputChange('academic', { target: { name: 'intendedInstitution', value: value } });
    };

    return (
        <>
            <Typography variant="h6" gutterBottom>4. Academic & Study Information</Typography>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <FormControl fullWidth required error={!!errors.currentStatus}>
                        <InputLabel>What is your current academic status?</InputLabel>
                        <Select
                            name="currentStatus"
                            value={data.currentStatus || ''}
                            label="What is your current academic status?"
                            onChange={(e) => handleInputChange('academic', e)}
                        >
                            <MenuItem value="highSchool">High School Learner (Grade 10-12)</MenuItem>
                            <MenuItem value="tertiary">Currently at Tertiary Institution</MenuItem>
                            <MenuItem value="notStudying">Not Currently Studying</MenuItem>
                        </Select>
                        {errors.currentStatus && <FormHelperText>{errors.currentStatus}</FormHelperText>}
                    </FormControl>
                </Grid>

                {/* Conditional Fields based on current status */}
                {data.currentStatus === 'highSchool' && (
                    <Grid item xs={12}>
                        <TextField
                            name="highSchoolName"
                            label="Name of High School"
                            fullWidth
                            value={data.highSchoolName || ''}
                            onChange={(e) => handleInputChange('academic', e)}
                        />
                    </Grid>
                )}
                {data.currentStatus === 'tertiary' && (
                    <>
                        <Grid item xs={12}>
                            {/* REPLACED TextField with Autocomplete for Current Institution */}
                            <Autocomplete
                                options={INSTITUTIONS.sort((a, b) => -b.category.localeCompare(a.category))}
                                groupBy={(option) => option.category}
                                getOptionLabel={(option) => option.label}
                                value={INSTITUTIONS.find(i => i.label === data.institutionName) || null}
                                onChange={handleInstitutionChange}
                                // Handle free text entry if they type something not in list (optional, but safer to strict list + 'Other')
                                isOptionEqualToValue={(option, value) => option.label === value.label}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        label="Name of Tertiary Institution" 
                                        fullWidth 
                                        required
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                name="currentLevel"
                                label="Current Level (e.g., 1st Year)"
                                fullWidth
                                value={data.currentLevel || ''}
                                onChange={(e) => handleInputChange('academic', e)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                name="nextLevel"
                                label="Intended Level for Next Year"
                                fullWidth
                                value={data.nextLevel || ''}
                                onChange={(e) => handleInputChange('academic', e)}
                            />
                        </Grid>
                    </>
                )}
                 {data.currentStatus === 'notStudying' && (
                    <Grid item xs={12}>
                        <TextField
                            name="highestQualification"
                            label="Highest Qualification Achieved"
                            fullWidth
                            value={data.highestQualification || ''}
                            onChange={(e) => handleInputChange('academic', e)}
                        />
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ mt: 2 }}>Intended Studies (Next Year)</Typography>
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        required
                        name="intendedCourse"
                        label="Course you intend to study"
                        fullWidth
                        value={data.intendedCourse || ''}
                        onChange={(e) => handleInputChange('academic', e)}
                        error={!!errors.intendedCourse}
                        helperText={errors.intendedCourse}
                    />
                </Grid>
                <Grid item xs={12}>
                     {/* REPLACED TextField with Autocomplete for Intended Institution */}
                     <Autocomplete
                        options={INSTITUTIONS.sort((a, b) => -b.category.localeCompare(a.category))}
                        groupBy={(option) => option.category}
                        getOptionLabel={(option) => option.label}
                        value={INSTITUTIONS.find(i => i.label === data.intendedInstitution) || null}
                        onChange={handleIntendedInstitutionChange}
                        renderInput={(params) => (
                            <TextField 
                                {...params} 
                                label="Name of Institution you intend to study at" 
                                fullWidth 
                                required
                                error={!!errors.intendedInstitution}
                                helperText={errors.intendedInstitution}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                     <FormControl component="fieldset" required error={!!errors.intendedQualification}>
                        <FormLabel component="legend">Qualification you intend to achieve</FormLabel>
                        <RadioGroup
                            row
                            name="intendedQualification"
                            value={data.intendedQualification || ''}
                            onChange={(e) => handleInputChange('academic', e)}
                        >
                            <FormControlLabel value="Certificate" control={<Radio />} label="National Certificate" />
                            <FormControlLabel value="Diploma" control={<Radio />} label="National Diploma" />
                            <FormControlLabel value="Degree" control={<Radio />} label="Degree" />
                        </RadioGroup>
                        {errors.intendedQualification && <FormHelperText>{errors.intendedQualification}</FormHelperText>}
                    </FormControl>
                </Grid>
                 <Grid item xs={12}>
                     <Typography variant="body1" sx={{ mb: 1 }}>Why did you choose this field of study?</Typography>
                     <TextField
                        required
                        name="motivation"
                        label="Motivation"
                        multiline
                        rows={4}
                        fullWidth
                        value={formData.motivation || ''}
                        onChange={(e) => handleInputChange('motivation', e)}
                        error={!!errors.motivation}
                        helperText={errors.motivation}
                    />
                </Grid>
            </Grid>
        </>
    );
};

export default AcademicInfoStep;